import { CustomerEventType } from "@redotech/redo-customer-event-common/customer-event-definition";
import { CustomerActivityType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";

/**
 * Customer events have event types. Categories are useful groupings of these
 * event types that are convenient for users to filter with.
 */
export enum CustomerActivityCategory {
  CLAIMS = "claims",
  ORDERS = "orders",
  RETURNS = "returns",
  SUPPORT = "support",
  SHOPPING = "shopping",
  MARKETING = "marketing",
  LOYALTY = "loyalty",
}

export type CustomerEventCategory =
  | CustomerActivityCategory.CLAIMS
  | CustomerActivityCategory.ORDERS
  | CustomerActivityCategory.RETURNS
  | CustomerActivityCategory.SUPPORT
  | CustomerActivityCategory.MARKETING
  | CustomerActivityCategory.SHOPPING
  | CustomerActivityCategory.LOYALTY;

export interface CustomerActivityFilterOptions {
  activityCategories?: CustomerActivityCategory[] | undefined;
  timestampStart?: Date | undefined;
  timestampEnd?: Date | undefined;
  limit?: number | undefined;
}

export function customerEventFilterOptionsToQueryParams(
  filterOptions: CustomerActivityFilterOptions | undefined,
): string | undefined {
  if (!filterOptions) {
    return undefined;
  }
  const queryParams: string[] = [];

  if (filterOptions.activityCategories) {
    queryParams.push(
      ...filterOptions.activityCategories.map(
        (category: CustomerActivityCategory) =>
          `eventCategories=${encodeURIComponent(category)}`,
      ),
    );
  }

  if (filterOptions.timestampStart) {
    queryParams.push(
      `timestampStart=${encodeURIComponent(filterOptions.timestampStart.toISOString())}`,
    );
  }

  if (filterOptions.timestampEnd) {
    queryParams.push(
      `timestampEnd=${encodeURIComponent(filterOptions.timestampEnd.toISOString())}`,
    );
  }

  if (filterOptions.limit) {
    queryParams.push(
      `limit=${encodeURIComponent(filterOptions.limit.toString())}`,
    );
  }

  return queryParams.join("&");
}

export const categoryToCustomerActivityTypes: {
  [key in CustomerActivityCategory]: CustomerEventType[];
} = {
  [CustomerActivityCategory.CLAIMS]: [
    CustomerEventType.CLAIM_CREATED,
    CustomerEventType.CLAIM_COMMENT_CREATED,
    CustomerEventType.CLAIM_STATUS_UPDATED,
  ],
  [CustomerActivityCategory.ORDERS]: [
    CustomerEventType.ORDER_CREATED,
    CustomerEventType.ORDER_UPDATED,
    CustomerEventType.ORDER_STATUS_UPDATED,
  ],
  [CustomerActivityCategory.RETURNS]: [
    CustomerEventType.RETURN_CREATED,
    CustomerEventType.RETURN_COMMENT_CREATED,
    CustomerEventType.RETURN_STATUS_UPDATED,
  ],
  [CustomerActivityCategory.SUPPORT]: [
    CustomerEventType.CONVERSATION_CREATED,
    CustomerEventType.CONVERSATION_MESSAGE_CREATED,
    CustomerEventType.CONVERSATION_STATUS_UPDATED,
  ],
  [CustomerActivityCategory.SHOPPING]: [
    CustomerEventType.PRODUCT_VIEWED,
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  ],
  [CustomerActivityCategory.MARKETING]: [
    CustomerEventType.MARKETING_SUBSCRIBED,
    CustomerEventType.MARKETING_CONFIRMED,
    CustomerEventType.MARKETING_UNSUBSCRIBED,
  ],
  [CustomerActivityCategory.LOYALTY]: [
    CustomerEventType.LOYALTY_EARN,
    CustomerEventType.LOYALTY_EARN_PURCHASE,
    CustomerEventType.LOYALTY_EARN_REVIEW,
    CustomerEventType.LOYALTY_REDEEM,
    CustomerEventType.LOYALTY_RETURN_REFUND,
    CustomerEventType.LOYALTY_RETURN_CLAWBACK,
    CustomerEventType.LOYALTY_CANCEL_PURCHASE_REFUND,
    CustomerEventType.LOYALTY_CANCEL_PURCHASE_CLAWBACK,
    CustomerEventType.LOYALTY_EXPIRE,
    CustomerEventType.LOYALTY_MANUAL_ADJUSTMENT,
  ],
};

export const customerActivityTypeToCustomerEventType: Record<
  CustomerActivityType,
  CustomerEventType
> = {
  [CustomerActivityType.RECEIVED_EMAIL]: CustomerEventType.OUTREACH_DELIVERY,
  [CustomerActivityType.OPENED_EMAIL]: CustomerEventType.OUTREACH_OPEN,
  [CustomerActivityType.CLICKED_EMAIL]: CustomerEventType.OUTREACH_CLICK,
  [CustomerActivityType.RECEIVED_TEXT]: CustomerEventType.OUTREACH_DELIVERY,
  [CustomerActivityType.CLICKED_TEXT]: CustomerEventType.OUTREACH_CLICK,
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]:
    CustomerEventType.PRODUCT_VIEWED,
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]:
    CustomerEventType.PRODUCT_VIEWED,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]:
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]:
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: CustomerEventType.ORDER_CREATED,
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]:
    CustomerEventType.ORDER_CREATED,
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]:
    CustomerEventType.CHECKOUT_STARTED,
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]:
    CustomerEventType.CHECKOUT_STARTED,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_BIGCOMMERCE]:
    CustomerEventType.PRODUCT_ADDED_TO_CART,
  [CustomerActivityType.CHECKOUT_STARTED_BIGCOMMERCE]:
    CustomerEventType.CHECKOUT_STARTED,
  [CustomerActivityType.ORDER_PLACED_BIGCOMMERCE]:
    CustomerEventType.ORDER_CREATED,
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]:
    CustomerEventType.STORE_PAGE_VIEWED,
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]:
    CustomerEventType.STORE_PAGE_VIEWED,
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]:
    CustomerEventType.COLLECTION_VIEWED,
  [CustomerActivityType.PAYMENT_FAILED_NO_ORDER_CREATED_SHOPIFY]:
    CustomerEventType.PAYMENT_FAILED,
  [CustomerActivityType.PAYMENT_FAILED_AFTER_ORDER_CREATED_SHOPIFY]:
    CustomerEventType.PAYMENT_FAILED,
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]:
    CustomerEventType.COLLECTION_VIEWED,
  [CustomerActivityType.RETURN_PROCESSED]: CustomerEventType.RETURN_PROCESSED,
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    CustomerEventType.SUBSCRIBED_TO_BACK_IN_STOCK,
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    CustomerEventType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION,
  [CustomerActivityType.BOUNCED_EMAIL]: CustomerEventType.OUTREACH_BOUNCE,
  [CustomerActivityType.REPLIED_SMS]: CustomerEventType.INBOUND_RECEIVED,
  [CustomerActivityType.MARKETING_SUBSCRIBED]:
    CustomerEventType.MARKETING_SUBSCRIBED,
  [CustomerActivityType.MARKETING_CONFIRMED]:
    CustomerEventType.MARKETING_CONFIRMED,
};
